import React, {useCallback, useEffect, useState} from 'react';
import AppBreadcrumb from "../../Shared/AppBreadcrumb";
import FecthingSpinner from "../../Shared/FecthingSpinner";
import httpService from "../../../services/http.service";
import {getRandomColor} from '../../../utils/index';
import ReactApexChart from "react-apexcharts";

const getCanonicalCountry = (country = '') => {
    const baseCountry = String(country).replace(/\s*\(.*?\)\s*/g, '').trim();
    const normalizedKey = baseCountry
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '');

    const countryAliases = {
        ivorycoast: "Cote d'Ivoire",
        cote_divoire: "Cote d'Ivoire",
        cotedivoire: "Cote d'Ivoire",
    };

    return {
        key: countryAliases[normalizedKey] || normalizedKey || 'inconnu',
        label: baseCountry || 'Inconnu',
    };
};

const mergeCountryDistribution = (countries = []) => {
    const grouped = countries.reduce((accumulator, item) => {
        const {key, label} = getCanonicalCountry(item?.country);
        const users = Number.parseInt(item?.users, 10) || 0;

        if (!accumulator[key]) {
            accumulator[key] = {
                country: label,
                users: 0,
            };
        }

        accumulator[key].users += users;
        return accumulator;
    }, {});

    return Object.values(grouped).sort((a, b) => b.users - a.users);
};

const UserActiveStats = ({history, match, location, ...props}) => {

    const [fetching, setFetching] = useState(true);
    const [charts, setCharts] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');

    const fetchActiveUsersStats = useCallback(async () => {
        try {
            setErrorMessage('');
            const [activeUsersResponse, recentUsersResponse] = await Promise.all([
                httpService.get(`/stats/top-active-users-by-country`),
                httpService.get(`/stats/top-recent-users-by-country`),
            ]);

            const activeUsersByCountry = mergeCountryDistribution(activeUsersResponse?.data?.data || []);
            const recentUsersByCountry = mergeCountryDistribution(recentUsersResponse?.data?.country_distribution || []);

            const activeUsersChart = {
                series: activeUsersByCountry.map((obj) => obj.users),
                options: {
                    title: {
                        text: "Top 10 pays avec le plus d'utilisateurs actifs",
                        align: 'center',
                        margin: 5,
                        offsetX: 0,
                        offsetY: 0,
                        floating: false,
                        style: {
                            fontSize: '14px',
                            fontWeight: 'bold',
                            fontFamily: undefined,
                            color: '#003e6f'
                        },
                    },
                    dataLabels: {
                        enabled: false,
                        dropShadow: {}
                    },
                    chart: {
                        width: "100%",
                        type: 'pie',
                        foreColor: '#000000'
                    },
                    colors: activeUsersByCountry.map(() => getRandomColor()),
                    labels: activeUsersByCountry.map((obj) => obj.country),
                    responsive: [{
                        breakpoint: 480,
                        options: {
                            legend: {
                                position: 'bottom'
                            }
                        }
                    }],
                    legend: {
                        position: 'bottom',
                        labels: {
                            colors: '#000000',
                            useSeriesColors: false
                        },
                        formatter: function (seriesName, opts) {
                            const percentage = opts.w.globals.seriesPercent[opts.seriesIndex]?.[0] || 0;
                            const users = opts.w.config.series[opts.seriesIndex] || 0;
                            return `<span style="color:#000000;">${seriesName} - ${Number.parseFloat(percentage).toFixed(2)}% (${users})</span>`;
                        },
                        markers: {
                            radius: 0
                        }
                    }
                },
            };

            const recentUsersChart = {
                series: recentUsersByCountry.map((obj) => obj.users),
                options: {
                    title: {
                        text: `Top 100 des pays avec le plus d'utilisateurs récents`,
                        align: 'center',
                        margin: 5,
                        offsetX: 0,
                        offsetY: 0,
                        floating: false,
                        style: {
                            fontSize: '14px',
                            fontWeight: 'bold',
                            fontFamily: undefined,
                            color: '#003e6f'
                        },
                    },
                    dataLabels: {
                        enabled: false,
                        dropShadow: {}
                    },
                    chart: {
                        width: "100%",
                        type: 'pie',
                        foreColor: '#000000'
                    },
                    colors: recentUsersByCountry.map(() => getRandomColor()),
                    labels: recentUsersByCountry.map((obj) => obj.country),
                    responsive: [{
                        breakpoint: 480,
                        options: {
                            legend: {
                                position: 'bottom'
                            }
                        }
                    }],
                    legend: {
                        position: 'bottom',
                        labels: {
                            colors: '#000000',
                            useSeriesColors: false
                        },
                        formatter: function (seriesName, opts) {
                            const percentage = opts.w.globals.seriesPercent[opts.seriesIndex]?.[0] || 0;
                            const users = opts.w.config.series[opts.seriesIndex] || 0;
                            return `<span style="color:#000000;">${seriesName} - ${Number.parseFloat(percentage).toFixed(2)}% (${users})</span>`;
                        },
                        markers: {
                            radius: 0
                        }
                    }
                },
            };

            setCharts([activeUsersChart, recentUsersChart]);
        } catch (e) {
            setCharts([]);
            setErrorMessage("Impossible de charger les statistiques pour le moment.");
        } finally {
            setFetching(false);
        }
    }, []);

    useEffect(() => {
        fetchActiveUsersStats();
    }, [fetchActiveUsersStats]);

    if(fetching) {
        return <FecthingSpinner />
    }

    return (
        <div className="container-fluid">
            <AppBreadcrumb title="Statistiques utilisateurs (Actifs)" />

            <div className="row clearfix">
                {errorMessage ? (
                    <div className="col-12">
                        <div className="card">
                            <div className="body text-center p-5">
                                <i className="fa fa-exclamation-triangle text-danger mb-3" style={{fontSize: 48}} />
                                <h4 className="mb-2">Erreur de chargement</h4>
                                <p className="text-muted mb-0">{errorMessage}</p>
                            </div>
                        </div>
                    </div>
                ) : charts.map((chart, index) => (
                    <div className="col-lg-6 col-md-12" key={index}>
                        <div className="card">
                            <div className="body">
                                <ReactApexChart options={chart.options} series={chart.series} type="pie"  />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )

};

export default UserActiveStats;